/**
 * The languages the creator surfaces (`/one` and `/join`) speak.
 *
 * Adding one is this list, a `messages/<locale>/` folder carrying every file
 * and key English has, and its loader in `request.ts`; the type check and
 * `tests/creatorI18n.test.ts` fail until all three agree. Framework is
 * not in scope: nothing outside the creator layouts reads this.
 */
export const CREATOR_LOCALES = ["en", "es"] as const;

export type CreatorLocale = (typeof CREATOR_LOCALES)[number];

export const DEFAULT_CREATOR_LOCALE: CreatorLocale = "en";

/**
 * Each language named in itself, never translated: someone who landed in a
 * language they don't read still recognises their own. Not in the catalogue
 * for the same reason; these words are the same on every screen.
 */
export const CREATOR_LOCALE_ENDONYMS: Record<CreatorLocale, string> = {
  en: "English",
  es: "Español",
};

/**
 * An explicit choice, set by a language picker once there is more than one
 * language to pick. It outranks the browser because it is the person saying
 * so, where `Accept-Language` is only their device guessing.
 */
export const CREATOR_LOCALE_COOKIE = "nf-creator-locale";

export function isCreatorLocale(
  value: string | null | undefined,
): value is CreatorLocale {
  return (CREATOR_LOCALES as readonly string[]).includes(value ?? "");
}

/**
 * The best supported language for a request: the cookie if it names one we
 * speak, else the highest-weighted `Accept-Language` entry we speak, matched
 * exactly (`pt-BR`) or by its base language (`es-MX` → `es`), else English.
 *
 * Generic over the supported list so the matching can be tested against
 * languages the product does not ship yet.
 */
export function negotiateLocale<T extends string>(
  {
    cookie,
    acceptLanguage,
  }: { cookie?: string | null; acceptLanguage?: string | null },
  supported: readonly T[],
  fallback: T,
): T {
  const find = (tag: string) =>
    supported.find((locale) => locale.toLowerCase() === tag.toLowerCase());

  const chosen = cookie ? find(cookie) : undefined;
  if (chosen) return chosen;

  const ranked = (acceptLanguage ?? "")
    .split(",")
    .map((part, index) => {
      const [tag, ...params] = part.trim().split(";");
      const q = params
        .map((param) => param.trim())
        .find((param) => param.startsWith("q="));
      const weight = q ? Number.parseFloat(q.slice(2)) : 1;
      return { tag, weight: Number.isNaN(weight) ? 0 : weight, index };
    })
    // Stable on ties, so the browser's own order breaks them.
    .filter(({ tag, weight }) => tag && tag !== "*" && weight > 0)
    .sort((a, b) => b.weight - a.weight || a.index - b.index);

  for (const { tag } of ranked) {
    const match = find(tag) ?? find(tag.split("-")[0]);
    if (match) return match;
  }
  return fallback;
}

export function negotiateCreatorLocale(input: {
  cookie?: string | null;
  acceptLanguage?: string | null;
}): CreatorLocale {
  return negotiateLocale(input, CREATOR_LOCALES, DEFAULT_CREATOR_LOCALE);
}
